import React, { useState, useEffect } from 'react';
import { useConfirm } from "../../hooks/useConfirm";
import { planosAPI } from '../../services/planosService';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { CreditCard, Plus, Edit, Trash2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageSkeleton, SkeletonGrid } from '../../components/Skeleton';

const AdminPlans = () => {
  const showConfirm = useConfirm();
  const { darkMode } = useTheme();
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlano, setEditingPlano] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    preco: 0,
    limite_usuarios: 1,
    limite_mensagens_mes: 1000,
    limite_campanhas: 5,
    limite_contatos: 500,
    limite_grupos: 10,
    limite_canais_whatsapp: 1,
    limite_conversas_ia_mes: 0,
    limite_fluxos: 0,
    creditos_meta_inclusos: 0,
    permite_ia: false,
    permite_fluxos: false,
    permite_relatorios_avancados: false,
    permite_integracao_api: false,
    permite_suporte_prioritario: false,
    permite_whatsapp_multiplo: false,
    permite_instagram: false,
    permite_inteligencia_competitiva: false,
    permite_monitoramento_hashtags: false,
    gerente_dedicado: false,
    ativo: true,
    visivel: true,
    ordem: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await planosAPI.listAdmin();
      setPlanos(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPlano) {
        await planosAPI.update(editingPlano.id, formData);
        toast.success('Plano atualizado com sucesso');
      } else {
        await planosAPI.create(formData);
        toast.success('Plano criado com sucesso');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar plano');
    }
  };

  const handleDelete = async (id) => {
    showConfirm({
      title: 'Desativar Plano',
      message: 'Tem certeza que deseja desativar este plano?',
      confirmText: 'Desativar',
      type: 'warning',
      onConfirm: async () => {
        try {
          await planosAPI.delete(id);
          toast.success('Plano desativado com sucesso');
          loadData();
        } catch (error) {
          toast.error('Erro ao desativar plano');
        }
      }
    });
  };

  const handleEdit = (plano) => {
    setEditingPlano(plano);
    setFormData({
      nome: plano.nome,
      descricao: plano.descricao || '',
      preco: plano.preco,
      limite_usuarios: plano.limite_usuarios,
      limite_mensagens_mes: plano.limite_mensagens_mes,
      limite_campanhas: plano.limite_campanhas,
      limite_contatos: plano.limite_contatos,
      limite_grupos: plano.limite_grupos,
      limite_canais_whatsapp: plano.limite_canais_whatsapp ?? 1,
      limite_conversas_ia_mes: plano.limite_conversas_ia_mes ?? 0,
      limite_fluxos: plano.limite_fluxos ?? 0,
      creditos_meta_inclusos: plano.creditos_meta_inclusos ?? 0,
      permite_ia: plano.permite_ia,
      permite_fluxos: plano.permite_fluxos,
      permite_relatorios_avancados: plano.permite_relatorios_avancados,
      permite_integracao_api: plano.permite_integracao_api,
      permite_suporte_prioritario: plano.permite_suporte_prioritario,
      permite_whatsapp_multiplo: plano.permite_whatsapp_multiplo,
      permite_instagram: plano.permite_instagram ?? false,
      permite_inteligencia_competitiva: plano.permite_inteligencia_competitiva ?? false,
      permite_monitoramento_hashtags: plano.permite_monitoramento_hashtags ?? false,
      gerente_dedicado: plano.gerente_dedicado ?? false,
      ativo: plano.ativo,
      visivel: plano.visivel ?? true,
      ordem: plano.ordem
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      preco: 0,
      limite_usuarios: 1,
      limite_mensagens_mes: 1000,
      limite_campanhas: 5,
      limite_contatos: 500,
      limite_grupos: 10,
      limite_canais_whatsapp: 1,
      limite_conversas_ia_mes: 0,
      limite_fluxos: 0,
      creditos_meta_inclusos: 0,
      permite_ia: false,
      permite_fluxos: false,
      permite_relatorios_avancados: false,
      permite_integracao_api: false,
      permite_suporte_prioritario: false,
      permite_whatsapp_multiplo: false,
      permite_instagram: false,
      permite_inteligencia_competitiva: false,
      permite_monitoramento_hashtags: false,
      gerente_dedicado: false,
      ativo: true,
      visivel: true,
      ordem: 0
    });
    setEditingPlano(null);
  };
  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Gerenciar Planos">
          <SkeletonGrid itens={6} colunas={3} />
        </PageSkeleton>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`text-2xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <CreditCard className="mr-2" />
              Gerenciar Planos
            </h1>
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Administre os planos do sistema
            </p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="secondary-button text-white px-4 py-2 rounded flex items-center"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Plano
          </button>
        </div>

        <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <table className="min-w-full">
            <thead className={darkMode ? 'bg-zinc-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Nome</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Preço</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Msgs/mês</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Canais</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Créditos Meta</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Funcionalidades</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Ações</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'bg-zinc-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {planos.map((plano) => (
                <tr key={plano.id} className={darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {plano.nome}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    R$ {parseFloat(plano.preco).toFixed(2)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {plano.limite_mensagens_mes === -1 ? '∞' : plano.limite_mensagens_mes?.toLocaleString()}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {plano.limite_canais_whatsapp === -1 ? '∞' : plano.limite_canais_whatsapp || 1} WA
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    R$ {parseFloat(plano.creditos_meta_inclusos || 0).toFixed(0)}
                  </td>
                  <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    <div className="flex flex-wrap gap-1">
                      {plano.permite_ia && <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">IA</span>}
                      {plano.permite_fluxos && <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">Fluxos</span>}
                      {plano.permite_instagram && <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 rounded">Instagram</span>}
                      {plano.permite_integracao_api && <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">API</span>}
                      {plano.gerente_dedicado && <span className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded">Gerente</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      plano.ativo 
                        ? darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                        : darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
                    }`}>
                      {plano.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                    {!plano.visivel && (
                      <span className={`ml-1 px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'}`}>
                        Oculto
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(plano)}
                        className={`p-2 rounded-md transition-colors ${
                          darkMode 
                            ? 'text-blue-400 hover:text-blue-300 hover:bg-zinc-700' 
                            : 'text-blue-600 hover:text-blue-900 hover:bg-gray-100'
                        }`}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(plano.id)}
                        className="p-2 rounded-md text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className={`rounded-lg p-6 w-full max-w-2xl m-4 ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {editingPlano ? 'Editar' : 'Novo'} Plano
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-700'}`}>Nome</label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-700'}`}>Preço (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.preco}
                      onChange={(e) => setFormData({...formData, preco: parseFloat(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-700'}`}>Descrição</label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    rows="2"
                  />
                </div>

                <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Limites (-1 = ilimitado)</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Usuários</label>
                    <input
                      type="number"
                      value={formData.limite_usuarios}
                      onChange={(e) => setFormData({...formData, limite_usuarios: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mensagens/mês</label>
                    <input
                      type="number"
                      value={formData.limite_mensagens_mes}
                      onChange={(e) => setFormData({...formData, limite_mensagens_mes: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Campanhas</label>
                    <input
                      type="number"
                      value={formData.limite_campanhas}
                      onChange={(e) => setFormData({...formData, limite_campanhas: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Contatos</label>
                    <input
                      type="number"
                      value={formData.limite_contatos}
                      onChange={(e) => setFormData({...formData, limite_contatos: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Grupos</label>
                    <input
                      type="number"
                      value={formData.limite_grupos}
                      onChange={(e) => setFormData({...formData, limite_grupos: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Canais WhatsApp</label>
                    <input
                      type="number"
                      value={formData.limite_canais_whatsapp}
                      onChange={(e) => setFormData({...formData, limite_canais_whatsapp: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Conversas IA/mês</label>
                    <input
                      type="number"
                      value={formData.limite_conversas_ia_mes}
                      onChange={(e) => setFormData({...formData, limite_conversas_ia_mes: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Fluxos</label>
                    <input
                      type="number"
                      value={formData.limite_fluxos}
                      onChange={(e) => setFormData({...formData, limite_fluxos: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Créditos Meta (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.creditos_meta_inclusos}
                      onChange={(e) => setFormData({...formData, creditos_meta_inclusos: parseFloat(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Ordem</label>
                    <input
                      type="number"
                      value={formData.ordem}
                      onChange={(e) => setFormData({...formData, ordem: parseInt(e.target.value)})}
                      className={`w-full border rounded px-3 py-2 ${darkMode ? 'border-gray-600 bg-zinc-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                </div>

                <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Funcionalidades</h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { key: 'permite_ia', label: 'IA Assistant' },
                    { key: 'permite_fluxos', label: 'Fluxos Conversacionais' },
                    { key: 'permite_relatorios_avancados', label: 'Relatórios Avançados' },
                    { key: 'permite_integracao_api', label: 'Integração API' },
                    { key: 'permite_suporte_prioritario', label: 'Suporte Prioritário' },
                    { key: 'permite_whatsapp_multiplo', label: 'WhatsApp Múltiplo' },
                    { key: 'permite_instagram', label: 'Instagram' },
                    { key: 'permite_inteligencia_competitiva', label: 'Inteligência Competitiva' },
                    { key: 'permite_monitoramento_hashtags', label: 'Monitoramento Hashtags' },
                    { key: 'gerente_dedicado', label: 'Gerente Dedicado' },
                  ].map(({ key, label }) => (
                    <label key={key} className={`flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={formData[key]}
                        onChange={(e) => setFormData({...formData, [key]: e.target.checked})}
                        className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div className="mb-4">
                  <label className={`flex items-center ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                    <input
                      type="checkbox"
                      checked={formData.ativo}
                      onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                      className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    Ativo
                  </label>
                  <label className={`flex items-center mt-2 ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                    <input
                      type="checkbox"
                      checked={formData.visivel}
                      onChange={(e) => setFormData({...formData, visivel: e.target.checked})}
                      className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    Visível para clientes
                  </label>
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className={`px-4 py-2 border rounded transition-colors ${
                      darkMode ? 'text-gray-300 border-gray-600 hover:bg-zinc-700' : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="gradient-button px-4 py-2 text-white rounded">
                    {editingPlano ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPlans;
